// shellGrammar — the primitives EVERY shell-door extractor needs.
//
// THIS PACKAGE IS THE CANONICAL HOME. A copy lives at
// `../../../src/lib/shellGrammar.js`, where eleven dashboard extractors depend
// on it; that copy is the one to retire once the dashboard can import from here.
// Until then, a change to either belongs in both — each function encodes a trap
// that cost real debugging, and a silent divergence reintroduces one.
//
// Extractors that read tool calls by NAME get a structured input object.
// Extractors that read SHELL calls get one opaque string that is a small
// program, and every one of them has independently hit the same traps:
//
//   1. VARIABLE INDIRECTION. Agents assign paths, scripts and flags to
//      variables and invoke through them. `-c "$cmds; qd"` read literally is a
//      two-step script, so a deep investigation reports as having done nothing.
//      The same blind spot hid a headless reverse-engineering pipeline from a
//      verb histogram for weeks.
//   2. MULTI-STATEMENT LINES. One call routinely carries setup, the real
//      invocation, and a post-filter. Matching a flag anywhere on the line
//      attributes another program's arguments to yours — and an ASSIGNMENT of a
//      program's path looks like a sighting while running nothing.
//   3. UNFLAGGED POSITIONAL ARGUMENTS. `cdb [options] <exe>`, `docker run
//      [opts] <image>`, `ssh [opts] <host>` — the most important argument often
//      has no flag, and a flag-driven parse reports no target at all.
//   4. INVERTED FILTERS. `grep -vE` / `Select-String -NotMatch` name what was
//      thrown AWAY. Read as positive they state the opposite of what happened,
//      and the switch appears on either side of the pattern and bundles with
//      other short flags.
//   5. CHROME IN THE PAYLOAD. Three separate sources of text that is not the
//      tool's own output, each of which shipped as "observed evidence" before
//      being caught: the HARNESS (a background-job notice), the SHELL (an error
//      about its own post-processing), and the TOOL's own startup banner. The
//      first two have corpora here; a tool's banner is domain-specific and
//      belongs in its extractor.
//
// Pure, quote-aware, deliberately coarse: the goal is to tell "this came from
// the command I am reading" from "this came from something else on the line",
// not to be a shell.

// Which tool NAMES are a shell door. Every shell-reading extractor needs this to
// declare its substrate — the scan window is filtered on tool names, and a lens
// that cannot name its door scans the newest N prompts and shows an empty panel
// whenever its work is sparse. Added when a second extractor reached outside its
// surface for it, which is the containment rule doing its job: a missing primitive
// announces itself instead of being copy-pasted.
export const SHELL_TOOL_NAMES = /^(?:bash|bash_tool|powershell|pwsh|shell|sh|zsh|cmd|run_command|run_terminal_cmd|execute_command|terminal|run_shell_command|run_in_terminal|local_shell_call)$/i;

// Text the HARNESS emits around a tool call, never the tool's own output.
// Shared with xrayAudit's boilerplate check — one corpus, so a notice learned
// by either side is known to both.
export const HARNESS_NOTICE_RE = new RegExp([
  "Command running in background with ID:",
  "Output is being written to:",
  "running in the background",
  "<system-reminder>",
  "\\[Request interrupted by user",
  "No content returned",
  "tool ran without output",
  "completed with no output",
  "The user doesn't want to (?:proceed|take) this action",
].join("|"), "i");

// Errors from the SHELL, not from the program it ran. A command that redirects
// its program's output to a file and then post-processes that file returns the
// post-processing's result — so when the read fails, the captured text is
// PowerShell's parser complaint and not the tool's output at all. Same
// consequence as a harness notice (a lens must not present it as observed
// evidence) but a different source, so it is a separate corpus.
export const SHELL_ERROR_RE = new RegExp([
  "^\\s*\\w[\\w-]*:\\s*\\n\\s*Line \\|",            // PowerShell cmdlet error block
  "\\+\\s+CategoryInfo\\s*:",                          // PowerShell error record
  "\\+\\s+FullyQualifiedErrorId\\s*:",
  "Cannot convert '[^']*' to the type",
  // `a?\\s*` could not match "as AN internal or external command" — this
  // alternative was dead until each one got its own test.
  "is not recognized as (?:the name of )?(?:an?\\s+)?(?:cmdlet|internal or external command)",
  "^\\s*\\S+: command not found",
  "^\\s*(?:bash|sh|zsh): .*: No such file or directory",
  "The term '[^']*' is not recognized",
  "ParserError:",
].join("|"), "im");

// Where a command sent its program's output. When this is set, the program's own
// stdout is NOT in the trace — whatever the tool returned describes something
// else, usually a later read of that file.
// Both halves need position anchoring, and the audit caught the cost of not
// having it: `-log` unanchored matches inside `.echo ===ETWCALLS-IN-LOGETW===`,
// which reported a redirect target of "ETW===" on 42 of 53 real ops. A flag must
// be a whole token, and `>` must sit where a redirection sits — otherwise an
// arrow in prose reads as one.
const REDIRECT_RE = /(?:(?:^|\s)-log(?:o)?\b\s*|(?:^|\s)\d?\*?>>?\s*)['"]?([^\s'"|;&<>]+)/i;

export function redirectTargetOf(text) {
  const m = REDIRECT_RE.exec(String(text || ""));
  return m ? m[1] : null;
}

// Terminal control sequences. Captured output carries them verbatim; they are
// rendering artifacts, not content, and they wreck line alignment on the
// reconstruction rail.
// eslint-disable-next-line no-control-regex
const ANSI_RE = /\u001b\[[0-9;]*[A-Za-z]|\[[0-9]{1,2}m/g;

export function stripAnsi(text) {
  return String(text ?? "").replace(ANSI_RE, "");
}

// A reference that names a variable rather than a value. Callers must report
// these as UNRESOLVED rather than as a target: two runs against `$dump` are
// almost certainly two different dumps, so grouping them invents a unit of work
// that never existed.
const VAR_REF_RE = /^(?:\$\{?\w|%\w+%|\$env:)/i;
export const isVarRef = (s) => VAR_REF_RE.test(String(s ?? ""));

// Quote-aware token split. Paths with spaces are the norm on Windows, and
// splitting on whitespace loses them.
export function tokenize(text) {
  const out = [];
  const re = /'([^']*)'|"([^"]*)"|(\S+)/g;
  for (let m = re.exec(String(text || "")); m; m = re.exec(String(text || ""))) {
    out.push(m[1] ?? m[2] ?? m[3]);
    if (re.lastIndex === 0) break;
  }
  return out;
}

// Split a line into statements, ignoring separators inside quotes — a debugger
// script or a regex is a semicolon/pipe-bearing list INSIDE one argument, and a
// naive split shreds every real command.
// SUPERSET of the dashboard's copy, and deliberately so: this one also treats a
// HEREDOC body as opaque and splits on `&&`. Mining reads Python payloads passed
// through `python3 - <<'PY'`, whose bodies are full of `|` and `;` belonging to
// the script — the simpler version shreds every one of them. The dashboard copy
// should adopt this; until it does, do not "simplify" this back to match it.
export function splitStatements(text) {
  const s = String(text || "");
  const spans = [];
  let start = 0;
  let quote = null;
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (quote) {
      if (c === quote) quote = null;
      i += 1;
      continue;
    }
    if (c === "'" || c === '"') { quote = c; i += 1; continue; }
    // A heredoc body is opaque: skip to its terminator so its contents never
    // fragment the surrounding command.
    if (c === "<" && s[i + 1] === "<") {
      const m = /^<<-?\s*(['"]?)([A-Za-z_]\w*)\1/.exec(s.slice(i));
      if (m) {
        const end = s.indexOf(`\n${m[2]}`, i);
        i = end < 0 ? s.length : end + m[2].length + 1;
        continue;
      }
    }
    if (c === ";" || c === "\n" || c === "|" || (c === "&" && s[i + 1] === "&")) {
      spans.push([start, i]);
      i += c === "&" ? 2 : 1;
      start = i;
      continue;
    }
    i += 1;
  }
  spans.push([start, s.length]);
  return spans.map(([a, b]) => ({ text: s.slice(a, b), at: a }));
}

const ASSIGN_RE = /(?:^|[\n;&|(])[\t ]*\$?([A-Za-z_]\w*)[\t ]*=[\t ]*(['"])((?:\\.|(?!\2)[\s\S])*)\2/g;
const RESOLVE_MAX_LEN = 20000;

// Inline literal variable assignments so the grammar reads what the program
// actually ran. A value produced by a pipeline or another command is left
// alone — unresolved is the honest answer, and `isVarRef` detects it.
export function resolveShellVars(command) {
  const cmd = String(command || "");
  if (!cmd || cmd.length > RESOLVE_MAX_LEN) return cmd;
  const vars = new Map();
  // Byte ranges of each assignment's own NAME. Substituting there turns
  // `$cdb = "C:\…cdb.exe"` into `C:\…cdb.exe = "C:\…cdb.exe"`, moving the
  // program's first sighting onto a line that never ran anything.
  const lhs = [];
  ASSIGN_RE.lastIndex = 0;
  for (let m = ASSIGN_RE.exec(cmd); m; m = ASSIGN_RE.exec(cmd)) {
    if (!vars.has(m[1])) vars.set(m[1], m[3]);
    const at = cmd.indexOf(m[1], m.index);
    if (at >= 0) lhs.push([at - 1, at + m[1].length]);
  }
  if (!vars.size) return cmd;
  // One pass only. A value that itself references a variable is left as
  // written — chasing depth buys nothing and risks a cycle.
  return cmd.replace(/\$\{(\w+)\}|\$(\w+)/g, (whole, braced, bare, offset) => {
    if (lhs.some(([a, b]) => offset >= a && offset < b)) return whole;
    const v = vars.get(braced || bare);
    return v === undefined ? whole : v;
  });
}

/**
 * The statement that actually RUNS the program, given regexes that spot its
 * name. Scoping every subsequent parse to this is what keeps another program's
 * flags out of your grammar.
 *
 * An ASSIGNMENT of the program's path looks like a sighting but executes
 * nothing, and the two forms fail differently: PowerShell `$cdb = "…"` matches
 * on the variable NAME, POSIX `CDB="…/cdb.exe"` matches inside the VALUE. Both
 * are rejected by requiring a real argument after the name.
 *
 * @returns {{text: string, at: number}|null} `at` is relative to `text`.
 */
export function anchorStatement(text, anchors) {
  const list = Array.isArray(anchors) ? anchors : [anchors];
  let fallback = null;
  for (const st of splitStatements(text)) {
    for (const re of list) {
      if (!re) continue;
      const at = st.text.search(re);
      if (at < 0) continue;
      if (fallback === null) fallback = { ...st, at };
      const rest = st.text.slice(at).replace(re, "");
      // Leading `=` means an assignment with spaces; only-quotes means an
      // assignment without them.
      if (/^\s*[^=\s]/.test(rest) && /[^\s"'`]/.test(rest)) return { ...st, at };
    }
  }
  return fallback;
}

/**
 * The first UNFLAGGED argument after the program name — the target of
 * `cdb [opts] <exe>`, `docker run [opts] <image>`, `ssh [opts] <host>`.
 *
 * @param opts.valueFlags  RegExp matching flags that CONSUME the next token.
 * @param opts.accept      RegExp the candidate must match (e.g. /\.exe$/i).
 * @param opts.reject      RegExp for candidates to skip (e.g. the program).
 */
export function firstBareArg(text, from, { valueFlags, accept, reject } = {}) {
  const toks = tokenize(String(text || "").slice(from > 0 ? from : 0));
  for (let i = 1; i < toks.length; i += 1) {
    const t = toks[i];
    if (t.startsWith("-") || t.startsWith("/")) {
      if (valueFlags?.test(t)) i += 1;
      continue;
    }
    // Redirections end the argument list. `&` does NOT — it is PowerShell's
    // call operator and precedes the program itself.
    if (/^(?:\d*>&?\d*|\d+)$/.test(t)) break;
    if (t === "&") continue;
    if (reject?.test(t)) continue;
    if (accept && !accept.test(t)) continue;
    return t;
  }
  return null;
}

// Flags are read on BOTH sides of the pattern: PowerShell writes the switch
// after it (`Select-String -Pattern "…" -NotMatch`), POSIX before and bundled
// (`grep -vE "…"`). Miss either and an exclusion is read as a finding schema.
const FILTER_RE = /\|\s*(?:[\w.\\/-]*\s*)?(?:Select-String|findstr|grep|rg|awk)\s+((?:[-/]\w+\s+)*)(?:-Pattern\s+)?['"]([^'"]{2,300})['"]((?:\s+[-/]\w+)*)/i;
// Length-bounded so a long word flag like `-Verbose` is not read as `-v`.
const NEGATED_RE = /(?:^|\s)(?:-NotMatch\b|-[a-z]{0,3}v[a-z]{0,3}\b|\/V\b)/i;

/**
 * The grep a command's output was piped through — a direct statement of what
 * the caller wanted out of a noisy stream, or (inverted) what they discarded.
 *
 * @returns {{terms: string[], negated: boolean}|null}
 */
export function pipedFilterOf(text, from = 0) {
  const s = String(text || "").slice(from > 0 ? from : 0);
  const m = FILTER_RE.exec(s);
  if (!m) return null;
  const terms = m[2].split("|").map((t) => t.trim()).filter(Boolean).slice(0, 24);
  if (!terms.length) return null;
  return { terms, negated: NEGATED_RE.test(`${m[1] || ""} ${m[3] || ""}`) };
}
